import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import FocusTrap from "focus-trap-react";
import noScroll from "no-scroll";
import { makeClassName, uniqueId } from "utils";
//import { Button } from "controls/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/pro-light-svg-icons/faTimes";

const DialogContext = React.createContext();

/**
 * A simple dialog wrapping arbitrary content.
 * By default, it shows a button that says "OK" (which closes the dialog)
 * Clicking outside of the dialog dismisses it without action.
 *
 * Example:
 * <Dialog>
 *   <Dialog.Content>
 *     Content goes here.
 *   </div>
 *   <Dialog.Footer>
 *     <Dialog.Action {...}>Button Text</Dialog.Action>
 *   </Dialog.Footer>
 * </Dialog>
 *
 * @property {String} title - The title of the dialog
 * @property {Function} shouldClose - Called when the dialog should be closed without action
 * @property {Boolean} show - True to show the dialg, false to hide it
 * @property {Boolean} disableBackdropClose - Set to false to disable clicking away to close
 * @property {Boolean} hideCloseButton - Set to false to disable the close button in the corner
 *
 * Deprecated properties:
 * @property {Boolean} allowScroll - See Dialog.Content
 * @property {Array} actions - Deprecated. Use Dialog.Footer and Dialog.Action.
 */
export default class Dialog extends Component {
  constructor(props) {
    super(props);
    this.dialogBox = React.createRef();
    this.uniqueIntForId = uniqueId();
  }

  componentDidMount() {
    if (this.props.show) {
      noScroll.on();
      this.dialogBox.current.focus();
    }
  }

  componentWillUnmount() {
    noScroll.off();
  }

  componentDidUpdate(prevProps) {
    if (this.props.show && this.props.show !== prevProps.show) {
      noScroll.on();
      this.dialogBox.current.focus();
    }
  }

  handleShowChange = e => {
    if (this.props.shouldClose) {
      e && e.preventDefault();
      this.props.shouldClose();
      noScroll.off();
    }
  };

  handleAction = action => {
    if (!action.noClose) {
      if (!action.isSubmit) {
        this.handleShowChange();
      } else {
        // Release the event queue, so the form's onSubmit can fire while the form is still
        // present on the page. Otherwise, there will be warning in the console.
        setTimeout(this.handleShowChange, 1);
      }
    }
  };

  handleBackdropClose = () => {
    if (!this.props.disableBackdropClose) {
      this.handleShowChange();
    }
  };

  handleCloseButtonKeyDown = evt => {
    if (evt.key === " " || evt.key === "Enter") {
      this.handleShowChange(evt);
    }
  };

  handleDialogKeyDown = evt => {
    if (evt.key === "Escape") {
      this.handleBackdropClose();
    }
  };

  /**
   * Renders the title section if a title is given.
   *
   * @returns {React.element}
   */
  renderTitle = () => {
    if (this.props.title) {
      return (
        <div className="ac-dialog__title">
          <div
            className="ac-dialog__title-text"
            id={`ac-dialog__title-text-${this.uniqueIntForId}`}
          >
            {this.props.title}
          </div>
          {!this.props.hideCloseButton && (
            <div
              className="ac-dialog__title-close"
              aria-label="Close Dialog"
              tabIndex={0}
              onClick={this.handleShowChange}
              onKeyDown={this.handleCloseButtonKeyDown}
            >
              <FontAwesomeIcon icon={faTimes} />
            </div>
          )}
        </div>
      );
    }
  };

  /**
   * Render the dialog content.
   *
   * @returns {React.element}
   */
  renderContent() {
    if (this.props.actions) {
      // props.actions is supported for backwards compatibility.
      // Actions can also be passed in as children, using Dialog.Footer and Dialog.Action.
      return <Fragment>
        <Dialog.Content noScroll={!this.props.allowScroll}>
          {this.props.children}
        </Dialog.Content>

        <Dialog.Footer>
          {this.props.actions.map((action, idx) =>
            <Dialog.Action {...action} key={action.label + idx}>
              {action.label}
            </Dialog.Action>
          )}
        </Dialog.Footer>
      </Fragment>;
    }

    return this.props.children;
  }

  /**
   * Render the component.
   *
   * @returns {React.element}
   */
  render() {
    if (this.props.show) {
      return (
        <FocusTrap>
          <div
            className="ac-dialog__container"
            onKeyDown={this.handleDialogKeyDown}
          >
            <div
              tabIndex={0}
              className={makeClassName(
                'ac-dialog',
                this.props.className
              )}
              ref={this.dialogBox}
              role="dialog"
              aria-modal="true"
              aria-labelledby={`ac-dialog__title-text-${this.uniqueIntForId}`}
            >
              {this.renderTitle()}

              <DialogContext.Provider value={{onClick: this.handleAction}}>
                {this.renderContent()}
              </DialogContext.Provider>
            </div>
            <div
              className="ac-dialog__backdrop"
              onClick={this.handleBackdropClose}
            />
          </div>
        </FocusTrap>
      );
    }
    return "";
  }
}

Dialog.defaultProps = {
  disableBackdropClose: false,
  hideCloseButton: false
};


/**
 * The dialog footer, meant to wrap <DialogAction/>.
 *
 * @param {Object} props - No properties are supported.
 * @returns {React.element}
 * @constructor
 */
Dialog.Footer = props => <div className="ac-dialog__actions">{props.children}</div>;

/**
 * The content of the dialog.
 *
 * @param {Object} props
 * @param {Boolean} props.noScroll - If the content is too tall, strech the dialog, rather than
 *   allowing the content to scroll. Use this when the dialog contains absolutely positioned
 *   elements (like <Form.Select>), because those elements will be cut off by the scrollable area.
 * @returns {React.element}
 * @constructor
 */
Dialog.Content = props => <div
  className={makeClassName(
    'ac-dialog__content',
    !props.noScroll && 'ac-dialog__allow-scroll'
  )}
>
  {props.children}
</div>;

/**
 * Create a button in the dialog.
 *
 * @property {Function} action - The function to exec when the user clicks the button.
 * @property {Boolean} isSubmit - This action is a submit button.
 * @property {Boolean} noClose - Don't close the dialog after this action.
 * @property {Boolean} loading - Show a loading spinner and disable the button.
 */
Dialog.Action = class DialogAction extends Component {
  handleAction(e, context) {
    if (!this.props.isSubmit) {
      e.preventDefault();
    }

    if (this.props.action) {
      this.props.action();
    }

    context.onClick(this.props);
  }

  renderAction = context => {
    if (!context || !context.onClick) {
      throw new Error('Dialog.Action can only be created inside a Dialog.');
    }

    return <Button
      isSubmit={this.props.isSubmit}
      onClick={e => this.handleAction(e, context)}
      loading={this.props.loading}
    >
      {this.props.children}
    </Button>;
  };

  render() {
    return <DialogContext.Consumer>{this.renderAction}</DialogContext.Consumer>;
  }
};

Dialog.Action.propTypes = {
  action: PropTypes.func,
  isSubmit: PropTypes.bool,
  noClose: PropTypes.bool,
  loading: PropTypes.bool
};

Dialog.Content.propTypes = {
  noScroll: PropTypes.bool
};

Dialog.propTypes = {
  title: PropTypes.string.isRequired,
  /**
   * Action types:
   *  Label - string - required - the label of the action
   *  Action - function - a function that handles the action
   *  isSubmit - boolean - whether or not this is a submit button
   *  loading - boolean - show a loading spinner on the button
   */
  actions: PropTypes.arrayOf(PropTypes.shape(Dialog.Action.propTypes)),
  /**
   * Called when the dialog should be closed without action
   */
  shouldClose: PropTypes.func,
  /**
   * True to show the dialog, false to hide it
   */
  show: PropTypes.bool.isRequired,
  /**
   * Set to false to disable clicking away to close
   */
  disableBackdropClose: PropTypes.bool,
  /**
   * Set to false to disable the close button in the corner
   */
  hideCloseButton: PropTypes.bool,
  /**
   * Enable scrolling
   */
  allowScroll: PropTypes.bool,
  className: PropTypes.string
};
